import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import Collapse from '@material-ui/core/Collapse';

import { tracking } from 'lib/tracking';
import { Button, Container, colors, spacing } from '@ownhealthil/wound-ui';
import { useTranslation, Trans } from 'i18n';

import theme from 'styles/theme';
import Spacer from 'components/Spacer';
import Text from 'components/Text';
import VStack from 'components/Stack/VStack';

import PlainTextButton from 'components/Button/PlainTextButton';
import ErrorImage from './error.png';
import CacheTableInstructions from './CacheTableInstructions';

const contentCss = css`
  flex-basis: 33%;
`;

const emailCss = css`
  color: inherit;
  font-weight: 500;
`;

const errorImageCss = css`
  width: 290px;

  @media (max-width: ${theme.breakpoints.values.sm}px) {
    width: 260px;
  }
`;

const centerPositionCss = css`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const titleCss = css`
  font-size: 3rem;
  text-align: center;
  margin: 0;
  line-height: 1.1;
  color: ${colors.text};
  padding-bottom: ${spacing.space4};

  @media (max-width: ${theme.breakpoints.values.sm}px) {
    font-size: 1.5rem;
  }
`;

const metadataCss = css`
  text-align: center;
  padding-bottom: ${spacing.space4};
`;

const mobileInfoCss = css`
  text-align: center;
  padding: 0 ${spacing.space10};

  & > a {
    color: ${colors.primary};
    text-decoration: underline;
    font-weight: 500;
  }
`;

const mobileSizeOnlyCss = css`
  display: contents;

  @media (min-width: ${theme.breakpoints.values.sm}px) {
    display: none;
  }
`;

const desktopSizeOnlyCss = css`
  display: contents;

  @media (max-width: ${theme.breakpoints.values.xs}px) {
    display: none;
  }
`;

const errorWrapperCss = css`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: ${theme.breakpoints.values.xs}px) {
    flex-direction: column;
  }
`;

function ErrorBoundaryPage({ error, resetError }) {
  const { t } = useTranslation();
  const [expand, setExpand] = useState(false);

  useEffect(() => {
    tracking.trackEvent({
      eventName: 'Error page',
      props: {
        attemptedUrl: window.location.pathname,
      },
    });
    if (expand) {
      tracking.trackEvent({
        eventName: 'Error page click did not help',
        props: {
          attemptedUrl: window.location.pathname,
        },
      });
    }
  }, [expand]);

  const handleExpand = () => {
    setExpand((prev) => !prev);
  };

  const handleRefresh = () => {
    resetError();
    tracking.trackEvent({
      eventName: 'Error page click refresh',
      props: {
        attemptedUrl: window.location.pathname,
      },
    });
  };

  const supportMailInfo = {
    to: process.env.CONTACT_US_EMAIL,
    subject: 'Bug Report spot-portal',
    body: {
      intro: '========== Auto generated ==========',
      error: error.toString(),
      browser: `userAgent: ${navigator.userAgent}`,
    },
  };

  const href = `mailto:${supportMailInfo.to}?subject=${supportMailInfo.subject}&body=%0D%0A%0D%0A%0D%0A${supportMailInfo.body.intro}%0D%0A${supportMailInfo.body.error}%0D%0A${supportMailInfo.body.browser}`;

  return (
    <Container size="md">
      <div css={centerPositionCss}>
        <div css={errorWrapperCss}>
          <Spacer size={3} />
          <VStack css={contentCss}>
            <h1 css={titleCss}>{t('errorPage.title')}</h1>
            <Text css={metadataCss}>{t('errorPage.subtitle')}</Text>

            <div css={mobileSizeOnlyCss}>
              <Text css={mobileInfoCss}>
                <Trans i18nKey="errorPage.help.info.mobile">
                  <a href="/">home</a>
                  <a href={href} target="_blank" rel="noreferrer">
                    link
                  </a>
                </Trans>
              </Text>
            </div>

            <div css={desktopSizeOnlyCss}>
              <Button onClick={handleRefresh}>
                {t('errorPage.primary.cta')}
              </Button>
              <Spacer size={1} />
              <PlainTextButton onClick={handleExpand}>
                {t('errorPage.secondary.cta')}
              </PlainTextButton>

              <Collapse
                css={metadataCss}
                in={expand}
                timeout={{
                  enter: 700,
                  exit: 500,
                }}
              >
                <Spacer size={1} />
                <Text>{t('errorPage.help.info')}</Text>
                <Spacer size={1} />
                <CacheTableInstructions />
                <Spacer size={1} />
                <Text>{t('errorPage.help.mail')}&nbsp;</Text>
                <a css={emailCss} href={href}>
                  <Text>{process.env.CONTACT_US_EMAIL}</Text>
                </a>
              </Collapse>
            </div>
            <Spacer size={3} />
          </VStack>
          <img src={ErrorImage} css={errorImageCss} alt="error" />
        </div>
      </div>
    </Container>
  );
}

ErrorBoundaryPage.propTypes = {
  error: PropTypes.object.isRequired,
  resetError: PropTypes.func.isRequired,
};

export default ErrorBoundaryPage;
